import { create } from 'zustand';
import { api } from '../lib/api-client';
import { useAuthStore } from './authStore';
import type { DriveSpineSet, Asset } from '@spine-workspace/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export interface SavedDriveFolder {
  folderId: string;
  folderName: string;
  lastImportedAt: string;
  assetIds: string[];
}

interface FolderContents {
  spineSets: DriveSpineSet[];
  folderName: string;
  folderId: string;
  debugFiles?: Array<{ name: string; mimeType: string }>;
}

interface DriveState {
  connected: boolean | null;
  checking: boolean;

  savedFolders: SavedDriveFolder[];

  // Folder the user explicitly picked + its contents
  pickedFolder: FolderContents | null;
  loadingFolder: boolean;
  folderError: string | null;

  importing: boolean;
  importError: string | null;

  checkConnection: () => Promise<void>;
  connectDrive: () => void;
  fetchSavedFolders: () => Promise<void>;
  inspectFolder: (folderId: string, folderName: string) => Promise<void>;
  clearPicked: () => void;
  importSpineSet: (params: {
    folderId: string;
    folderName: string;
    assetId?: string;
    label?: string;
    jsonFileId?: string;
    atlasFileId?: string;
    pngFileIds?: string[];
  }) => Promise<{ asset: Asset; versionNumber: number }>;
}

export const useDriveStore = create<DriveState>((set, get) => ({
  connected: null,
  checking: false,
  savedFolders: [],
  pickedFolder: null,
  loadingFolder: false,
  folderError: null,
  importing: false,
  importError: null,

  checkConnection: async () => {
    set({ checking: true });
    try {
      const res = await api.get<{ connected: boolean }>('/api/drive/status');
      set({ connected: res.connected });
      if (res.connected) {
        get().fetchSavedFolders();
      }
    } catch {
      set({ connected: false });
    } finally {
      set({ checking: false });
    }
  },

  connectDrive: () => {
    const token = useAuthStore.getState().token;
    window.location.href = `${BASE_URL}/api/auth/google/drive?token=${encodeURIComponent(token ?? '')}`;
  },

  fetchSavedFolders: async () => {
    try {
      const res = await api.get<{ folders: SavedDriveFolder[] }>('/api/drive/saved-folders');
      set({ savedFolders: res.folders });
    } catch {
      // silent — saved folders are best-effort
    }
  },

  inspectFolder: async (folderId, folderName) => {
    set({ loadingFolder: true, folderError: null, pickedFolder: null });
    try {
      const res = await api.get<{
        spineSets: DriveSpineSet[];
        spineSet: DriveSpineSet | null;
        _debug?: { topFiles: Array<{ name: string; mimeType: string }>; subFolders: Array<{ name: string; id: string }> };
      }>(`/api/drive/folders/${folderId}/files`);

      const spineSets = res.spineSets ?? (res.spineSet ? [res.spineSet] : []);

      set({
        pickedFolder: {
          folderId, folderName, spineSets,
          debugFiles: res._debug?.topFiles,
        },
      });

      if (spineSets.length === 0) {
        set({ folderError: 'No Spine export found (.json + .atlas + .png required)' });
      }
    } catch (e) {
      set({ folderError: e instanceof Error ? e.message : 'Could not read folder' });
    } finally {
      set({ loadingFolder: false });
    }
  },

  clearPicked: () => set({ pickedFolder: null, folderError: null }),

  importSpineSet: async ({ folderId, folderName, assetId, label, jsonFileId, atlasFileId, pngFileIds }) => {
    set({ importing: true, importError: null });
    try {
      const res = await api.post<{ asset: Asset; versionNumber: number }>(
        '/api/drive/import',
        { folderId, folderName, assetId, label, jsonFileId, atlasFileId, pngFileIds }
      );
      return res;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Import failed';
      set({ importError: msg });
      throw e;
    } finally {
      set({ importing: false });
    }
  },
}));

// Parse a Google Drive folder ID from a URL like:
// https://drive.google.com/drive/folders/1abc...xyz
// https://drive.google.com/drive/u/0/folders/1abc...xyz
export function parseDriveFolderUrl(input: string): { folderId: string; folderName: string } | null {
  const trimmed = input.trim();

  // If it looks like a bare folder ID (no slashes, alphanumeric + hyphens, 25-44 chars)
  if (/^[a-zA-Z0-9_-]{25,44}$/.test(trimmed)) {
    return { folderId: trimmed, folderName: 'Drive folder' };
  }

  // Try parsing as a URL
  try {
    const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    const match = url.pathname.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    if (match) {
      return { folderId: match[1], folderName: 'Drive folder' };
    }
  } catch {
    // Not a valid URL
  }

  return null;
}
